import * as DateFns from 'date-fns';
import {
  AFC,
  ALL_SEASON_MONTHS,
  FILTER_KEYS,
  GAME,
  LEAGUES,
  MINUTES_WAITING_DATA_TIME,
  NFC,
  NFL,
  NFL_TEAMS_GROUPS,
  OFF_SEASON_MONTHS,
  PAST_PRACTICE,
  PRACTICE,
  SCOPE,
  TEAMS_ALL,
  TEAMS_COLORS
} from '../config/constants';
import { RangeYYMMDDDDate, TeamData } from '../config/commonTypes';
import { GlobalFilterVenuesType, Intensity, IntensityUI, PeriodType, SeasonWeeks, PeriodTypeUI } from '../store/types';

export const routeToScope = (route: string): string => {
  const routes = route.split('/');
  const walking = (scope: any, i: number): string => {
    if (!(scope.constructor === Object)) {
      return scope;
    }
    if (routes.length - 1 === i) {
      return scope.scope(routes[i]);
    }
    return walking(scope.scope(routes[i]), i + 1);
  };
  return walking(SCOPE, 1) || '';
};

export const routeToSpecificScope = (route: string | null): string => {
  if (typeof route !== 'string') {
    return '';
  }
  const routes = route.split('/');
  const scope = routeToScope(route);
  if (routes[1] === 'view' && routes[2] === 'game' && routes[7] && routes[7] === 'modal') {
    return scope + ':' + routes[3] + ':' + routes[4] + ':' + routes[8];
  }
  if (routes[1] === 'view' && routes[2] === 'game') {
    return scope + ':' + routes[3] + ':' + routes[4];
  }
  if (routes[1] === 'view' && routes[2] === 'practice' && routes[6] && routes[6] === 'modal') {
    return scope + ':' + routes[3] + ':' + routes[7];
  }
  if (routes[1] === 'view' && routes[2] === 'practice' && routes[5] === 'metrics') {
    return scope + ':' + routes[3] + ':' + routes[8];
  }
  if (routes[1] === 'view' && routes[2] === 'practice') {
    return scope + ':' + routes[3];
  }
  if (routes[1] === 'practice') {
    return scope + ':' + routes[3];
  }
  if (routes[1] === 'player') {
    return scope + ':' + routes[2];
  }
  if (routes[1] === 'team') {
    return scope + ':' + routes[2] + ':' + routes[3];
  }
  if (routes[1] === SCOPE.DATAOPS.PARAMS.ROOT && routes[2] === SCOPE.DATAOPS.PARAMS.ENTITIES) {
    return scope + ':' + routes[3];
  }
  return scope || '';
};

export const routeToTableSpecificScope = (route: string | null) => {
  if (!route) {
    return '';
  }
  return routeToSpecificScope(route.replace('modal', 'page'));
};
/**
 * Return true if element is in array
 * @param {Array<any>} array
 * @param {any}        element
 * @returns {boolean}
 */
export const isIn = <T>(array: T[], element: T) => {
  return array.some((item) => JSON.stringify(item) === JSON.stringify(element));
};

export const routeToViewPracticeGamePageScope = (route: string | null): string => {
  if (typeof route === 'string') {
    const routes = route.replace('modal', 'page').split('/');//.slice(0, 7)
    const event = routeToEventType(route);
    if (routes[1] === 'view' && (event === GAME || event === PRACTICE)) {
      return routeToSpecificScope(routes.join('/')).replace('_CONDENSED', '').replace('_METRICS', '').replace('_EXPANDED', '').replace(':undefined', '');
    }
  }
  return '';
};

export const routeToEventType = (route: string | null): string => {
  if (typeof route === 'string') {
    return route.split('/')[2];
  }
  return '';
};

export const routeIsGame = (route: string): boolean => {
  return routeToEventType(route) === GAME;
};

export const routeToViewPracticeGameFilterForceScope = (route: string) => {
  const scopePracticeGame = routeToViewPracticeGamePageScope(route);
  const forceScope = scopePracticeGame.split(':');
  if (forceScope.length > 2) { // route for games or metrics page
    forceScope.pop(); // deleting :TEAM_ID or :METRIC_KEY
  }
  return forceScope.join(':');
};

export const modifyTime = (time: Date, hours: number, minutes: number, seconds: number, isAm: boolean) => {
  if (isAm) {
    if (hours === 12) {
      hours = 0;
    }
  } else {
    if (hours !== 12) {
      hours += 12;
    }
  }
  let newTime = time;
  newTime = DateFns.setHours(newTime, hours);
  newTime = DateFns.setMinutes(newTime, minutes);
  newTime = DateFns.setSeconds(newTime, seconds);
  return newTime;
};

export const isJustFinished = (practiceMeta: any): boolean => {
  return practiceMeta && practiceMeta.type === PAST_PRACTICE && (practiceMeta.processingStatus === 'INPROGRESS' || 
  DateFns.differenceInMinutes(new Date(), new Date(practiceMeta.endTime)) < 3);
};

export const cloneObject = (object: any): any => {
  return JSON.parse(JSON.stringify(object));
};

export const getDataScope = (teamId: string) => (teamId ? ('data:' + teamId) : 'data');

export const isJson = (str: any): str is string => {
  try {
    return (JSON.parse(str) && !!str);
  } catch (e) {
    return false;
  }
};

export const getTeamData = (teamId?: string, league?: string): TeamData | null => {
  let teamData = null;
  if (league === NFL) {
    for (const conference of [NFC, AFC]) {
      for (const division in NFL_TEAMS_GROUPS[conference as typeof NFC | typeof AFC]) {
        NFL_TEAMS_GROUPS[conference as typeof NFC | typeof AFC][division].forEach((team) => {
          if (team.teamId === '' + teamId) {
            teamData = {
              ...team,
              conference,
              division,
              color: TEAMS_COLORS[team.teamAbbrName] ?
                TEAMS_COLORS[team.teamAbbrName] : { text: '#FFFFFF', line: '#000000' },
              isNCAA: false
            };
          }
        });
      }
    }
  } else {
    TEAMS_ALL.forEach(team => {
      if (team.teamId === '' + teamId) {
        teamData = {
          ...team,
          teamAbbrName: team.teamAbbr,
          color: TEAMS_COLORS[team.teamAbbr] ? TEAMS_COLORS[team.teamAbbr] : { text: 'gray', line: '#000000' },
          isNCAA: (team.league === 'ncaa-fbs' || team.league === 'ncaa-mlax' || team.teamId === '194' || team.teamId === '19410')
        };
      }
    });
  }
  return teamData;
};

/**
 * Return all date ranges depending weeks, with off season months or is ncaa
 * @returns {[{start: 'yyyy-MM-dd', end: 'yyyy-MM-dd'}]}
 */
export const getAllDateRanges = (year: number, weeks: SeasonWeeks | undefined, isNcaa: boolean, withOffSeasonMonths: boolean): RangeYYMMDDDDate[] => {
  const dateRanges = [];
  if (isNcaa) {
    dateRanges.push(...ALL_SEASON_MONTHS[year].map(month => ({
      start: month.startOfMonth,
      end: month.endOfMonth
    })));
  } else {
    dateRanges.push(...((weeks && weeks[year]) || []).map(week => ({
      start: week.startOfWeek,
      end: week.endOfWeek
    })));
    if (withOffSeasonMonths) {
      dateRanges.push(...OFF_SEASON_MONTHS[year].map(month => ({
        start: month.startOfMonth,
        end: month.endOfMonth
      })));
    }
  }
  return dateRanges;
};

export const getVenues = (venues: GlobalFilterVenuesType | undefined, league: string, type: typeof PRACTICE | typeof GAME | string) => {
  if (venues) {
    switch (league) {
      case LEAGUES.NCAA:
        if (type === PRACTICE) {
          return venues.ncaaFbs.practice;
        } else {
          return venues.ncaaFbs.game;
        }
      case LEAGUES.NFL:
        if (type === PRACTICE) {
          return venues.nfl.practice;
        } else {
          return venues.nfl.game;
        }
      case LEAGUES.MLAX:
        return venues.mlax.practice;
      default:
        return [];
    }
  }
  return [];
};

export const buildIntensityFilter = (intensities: Intensity[]) => {
  return {
    name: "Intensity",
    types: intensities.map((intensity) => ({
      value: intensity.name,
      name: intensity.name,
    })),
    title: "INTENSITY",
    filterKey: FILTER_KEYS.INTENSITY,
  };
};

export const buildPeriodTypesFilter = (periodTypes: PeriodType[]) => {
  return {
    name: 'Practice Periods',
    types: periodTypes.map(periodtype => periodtype.name),
    title: 'PRACTICE PERIODS',
  };
};

export  const hasEmptyName= (data:IntensityUI[] | PeriodTypeUI[]) => {
    return data.some(item => !item.name || item.name.trim() === '');
  };